/**
 * The entry screen's connection to the draft store: debounce, queue, flush.
 *
 * One hook owns the whole lifecycle so the screen never has to reason about
 * IndexedDB or about which of the two send paths applies:
 *
 * **Online, per settled entry** — debounce ~800ms, then one
 * `PUT …/lines/{item_no}`. Small and immediate, and the natural fit for one field.
 * The debounce is what stops a request going out behind every digit of `1948`.
 *
 * **Offline, or after any failure** — the draft stays `pending`, and on reconnect
 * one `PUT …/lines` carries **the current state of every drafted item**. That is
 * what the batch endpoint already wants ("send the current state of the count, not
 * a diff"), and it is what makes a flush idempotent and safe to repeat after a
 * broken reply — the one retry § 4.3 permits, because entry is a full-state write
 * and posting is not.
 *
 * Two things this hook deliberately does not do: it never flushes a session
 * staging has frozen (§ 4.4 — explicit discard only), and it never touches the
 * post. Posting has one rule and it is somewhere else.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "../api/client";
import type { CountSession, LineSync } from "../api/types";
import {
  discardDrafts,
  markFullySynced,
  putDraft,
  readDrafts,
  setDraftState,
} from "./draftStore";
import type { DraftRecord } from "./db";
import { canFlush, planFlush, type FlushBlock, type Overwrite } from "./reconcile";
import { useOnline } from "./useOnline";

/** § 4.2: "debounce ~800ms, then PUT for that item." */
export const DEBOUNCE_MS = 800;

export interface DraftSyncState {
  online: boolean;
  /**
   * False until the draft store has actually been read.
   *
   * Gates on `pending` are only meaningful after this. Before it, `pending` is 0
   * because nothing has been *looked at* — not because nothing is held — and the
   * review screen briefly opening on that would show totals missing the manager's
   * last entries, which is the exact thing the gate exists to prevent.
   */
  loaded: boolean;
  drafts: Map<string, DraftRecord>;
  pending: number;
  refused: number;
  /** Differences the last pull-then-replay overwrote (§ 4.4). */
  overwrites: Overwrite[];
  /** Per-item warnings the server returned for a sync. */
  warnings: Map<string, string>;
  /** Warnings from the last batch flush, which may name several items. */
  flushWarnings: string[];
  flushing: boolean;
  /** Why a flush cannot run, when it cannot. */
  block: FlushBlock;
  /** Queue a typed value. `null` means "not counted". */
  record: (
    itemNo: string,
    countedQty: string | null,
    unitOfMeasureCode?: string | null,
  ) => Promise<void>;
  /** Send everything held, now. */
  flush: () => Promise<void>;
  /** Throw away what is held, on the manager's explicit say-so. */
  discard: () => Promise<number>;
  dismissOverwrites: () => void;
}

export function useDraftSync(
  areaId: string,
  session: CountSession,
  principal: string | null,
): DraftSyncState {
  const online = useOnline();
  const queryClient = useQueryClient();
  const sessionId = session.session_id;

  const [drafts, setDrafts] = useState<Map<string, DraftRecord>>(new Map());
  const [overwrites, setOverwrites] = useState<Overwrite[]>([]);
  const [warnings, setWarnings] = useState<Map<string, string>>(new Map());
  const [flushWarnings, setFlushWarnings] = useState<string[]>([]);
  const [flushing, setFlushing] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // `session` changes identity on every pull, and the flush closure needs the
  // latest lines without being re-created (which would re-arm the reconnect
  // effect and could flush twice).
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const flushingRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const rows = await readDrafts(sessionId);
      setDrafts(new Map(rows.map((row) => [row.item_no, row])));
      return rows;
    } finally {
      setLoaded(true);
    }
  }, [sessionId]);

  useEffect(() => {
    setLoaded(false);
    void refresh();
  }, [refresh]);

  const applySync = useCallback(
    (result: LineSync) => {
      // The reply carries the whole updated session, so the cache is *set* from it
      // rather than invalidated: re-reading a 289-line session over cooler WiFi
      // after every entry is exactly the cost this avoids.
      queryClient.setQueryData(["count", "areas", areaId, "sessions", sessionId], result.session);
    },
    [areaId, queryClient, sessionId],
  );

  /** One item, one request. The online path. */
  const sendOne = useCallback(
    async (itemNo: string, countedQty: string | null, unitOfMeasureCode?: string | null) => {
      try {
        // The unit is omitted, not sent as null, when the manager never chose one
        // (item 3). A base-unit line is then byte-identical on the wire to what it
        // was before item 3, and the staging table applies its own base default.
        const body: Record<string, unknown> = { item_no: itemNo, counted_qty: countedQty };
        if (unitOfMeasureCode) body.unit_of_measure_code = unitOfMeasureCode;
        const result = await apiFetch<LineSync>(
          `/count/areas/${encodeURIComponent(areaId)}/sessions/${encodeURIComponent(
            sessionId,
          )}/lines/${encodeURIComponent(itemNo)}`,
          {
            method: "PUT",
            body: JSON.stringify(body),
          },
        );
        await setDraftState(sessionId, itemNo, "synced");
        applySync(result);
        // The draft is KEPT, marked synced — § 4.1 lists `synced` as a state, and
        // it is what lets the row say "saved" instead of going quiet the moment it
        // succeeds. Stale synced drafts are pruned at the next pull, where staging
        // is in hand to compare against.
        if ((await readDrafts(sessionId)).every((d) => d.sync_state === "synced")) {
          await markFullySynced(sessionId);
        }
        // Per-item warnings — a lot-tracked surplus with no open lot, a non-base
        // UOM. Each is a line the AL will refuse at posting if the count came out
        // high, so it is said at entry time instead of discovered at review.
        if (result.warnings.length) {
          setWarnings((prev) => new Map(prev).set(itemNo.trim().toUpperCase(), result.warnings.join(" ")));
        }
      } catch (error) {
        // Refused, or the transport broke. Either way the draft stays and the
        // manager keeps their number; a flush will try again.
        await setDraftState(
          sessionId,
          itemNo,
          "refused",
          error instanceof ApiError
            ? error.detail
            : `Not saved yet: ${(error as Error).message}. It is still held on this device.`,
        );
      }
      await refresh();
    },
    [applySync, areaId, refresh, sessionId],
  );

  /**
   * Persist a typed value, then send it if there is a connection.
   *
   * **Sends immediately.** The ~800ms debounce lives in the entry screen, where
   * the keystrokes are; a second one here would delay the two actions that are
   * already deliberate — leaving a field, and clearing a count — by another
   * eight-tenths of a second each, and a clear that appears not to have happened
   * invites a second tap.
   */
  const record = useCallback(
    async (itemNo: string, countedQty: string | null, unitOfMeasureCode?: string | null) => {
      await putDraft(sessionId, itemNo, countedQty, unitOfMeasureCode);
      await refresh();
      if (!navigator.onLine) return; // Queued. The reconnect flush carries it.
      await sendOne(itemNo.trim().toUpperCase(), countedQty, unitOfMeasureCode);
    },
    [refresh, sendOne, sessionId],
  );

  /**
   * Send everything held, as one full-state batch.
   *
   * Idempotent by construction, which is why this is the one write in the app that
   * may be repeated after a broken reply: it sends the current state of every
   * drafted item, so applying it twice lands the same staging rows.
   */
  const flush = useCallback(async () => {
    if (flushingRef.current) return;

    const rows = await readDrafts(sessionId);
    const unsynced = rows.filter((row) => row.sync_state !== "synced");
    const gate = canFlush(
      sessionRef.current,
      principal,
      navigator.onLine !== false,
      unsynced.length,
    );
    if (!gate.allowed) return;

    const plan = planFlush(rows, sessionRef.current.lines);
    if (plan.entries.length === 0) {
      // Everything held already matches staging — nothing to send, and the session
      // is fully synced by definition.
      await markFullySynced(sessionId);
      await refresh();
      return;
    }

    flushingRef.current = true;
    setFlushing(true);
    try {
      const result = await apiFetch<LineSync>(
        `/count/areas/${encodeURIComponent(areaId)}/sessions/${encodeURIComponent(
          sessionId,
        )}/lines`,
        { method: "PUT", body: JSON.stringify({ entries: plan.entries }) },
      );
      for (const entry of plan.entries) {
        await setDraftState(sessionId, entry.item_no, "synced");
      }
      applySync(result);
      await markFullySynced(sessionId);
      if (plan.overwrites.length) setOverwrites(plan.overwrites);
      if (result.warnings.length) setFlushWarnings(result.warnings);
    } catch (error) {
      const detail =
        error instanceof ApiError
          ? error.detail
          : `The sync did not complete: ${(error as Error).message}. Everything is still held on this device.`;
      for (const entry of plan.entries) {
        await setDraftState(sessionId, entry.item_no, "refused", detail);
      }
    } finally {
      flushingRef.current = false;
      setFlushing(false);
      await refresh();
    }
  }, [applySync, areaId, principal, refresh, sessionId]);

  // Flush when the connection returns, and when the app is brought back in front
  // of someone. Both are moments a manager expects the queue to move.
  useEffect(() => {
    const attempt = () => void flush();
    window.addEventListener("online", attempt);
    const onVisible = () => {
      if (document.visibilityState === "visible") attempt();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", attempt);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [flush]);

  const discard = useCallback(async () => {
    const dropped = await discardDrafts(sessionId);
    await refresh();
    return dropped;
  }, [refresh, sessionId]);

  const rows = [...drafts.values()];
  const pending = rows.filter((row) => row.sync_state !== "synced").length;

  return {
    online,
    loaded,
    drafts,
    pending,
    refused: rows.filter((row) => row.sync_state === "refused").length,
    overwrites,
    warnings,
    flushWarnings,
    flushing,
    block: canFlush(session, principal, online, pending),
    record,
    flush,
    discard,
    dismissOverwrites: () => setOverwrites([]),
  };
}
